import React from "react";
import Immutable from "immutable";

import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as Groups from "js/common/groups";
import * as Rata from "js/common/utils/remote-data";
import * as Users from "js/common/users";
import {DEFAULT_GAMIFIED_ZOOM} from "js/dashboards/settings/appearance-utils";
import * as SharingUtils from "js/common/views/sharing/utils";


export const dashboardColumns = 48;

// A zoomed panel shows the same content at `zoom` times the size while a grid unit stays
// the same physical size, so it needs that many more units to hold its text. Capped at
// the template's own maximum, which is what the grid would clamp it to anyway.
export const scaleInitLayoutForZoom = (dimensions, zoom, max) => {
  if (!dimensions || !(zoom > 1)) {
    return dimensions;
  }
  const scale = key => {
    const scaled = Math.round(dimensions.get(key) * zoom);
    const limit = max ? max.get(key) : null;
    return Number.isFinite(limit) ? Math.min(scaled, limit) : scaled;
  };
  return dimensions.set("width", scale("width")).set("height", scale("height"));
};
export const columnSpacing = 13;

export const GAMIFIED_ASPECT_RATIOS = {
  "16:9":  {w: 16, h: 9,  description: "Widescreen TV & Monitor"},
  "16:10": {w: 16, h: 10, description: "Laptop & desktop monitor"},
  "4:3":   {w: 4,  h: 3,  description: "Conference room projector"},
  "21:9":  {w: 21, h: 9,  description: "Ultrawide display"},
};

// Horizontal padding either side of the stage, so it never triggers a horizontal scrollbar.
export const STAGE_H_PADDING = 24;

// Number of grid rows that keeps square cells for a given aspect ratio at 48 columns
export const getGamifiedMaxRows = aspectRatio => {
  const ratio = GAMIFIED_ASPECT_RATIOS[aspectRatio] || GAMIFIED_ASPECT_RATIOS["16:9"];
  return Math.round(dashboardColumns * ratio.h / ratio.w);
};

// Stage dimensions in CSS pixels, derived from the content width measured in app.js.
//
// Dividing by `zoom` is what makes zoom useful rather than merely magnifying: CSS zoom
// scales the stage back up to the measured width, so the dashboard still fits the
// viewport, but the stage laid out in fewer CSS pixels gives every panel fewer of them
// too — so a 14px label renders at 14 * zoom physical pixels. Without this division the
// stage would simply overflow a viewport it cannot scroll (the gamified scroll container
// is overflow: hidden).
//
// The padding is subtracted before the division because it lives inside the zoomed
// element and is therefore magnified too; taking it off first keeps the rendered stage
// box identical at every zoom level. The caller divides its CSS padding to match.
export const getGamifiedStageGeometry = ({width, zoom = 1, maxRows}) => {
  // The floor is applied before the division so it constrains the RENDERED width: the
  // stage must never come out wider than the container it cannot scroll.
  const stageWidth = Math.max((width || 800) - 2 * STAGE_H_PADDING, 400) / zoom;
  // react-grid-layout's spacing constants are absolute pixels, so they have to shrink
  // alongside the layout space — otherwise at high zoom the margins consume the whole
  // stage and the row height collapses through zero. The floor of 1 keeps a viewport
  // too narrow for 48 columns degrading gracefully rather than going negative.
  const rowHeight = Math.max(stageWidth / dashboardColumns - columnSpacing / zoom, 1);
  // Matches react-grid-layout's internal calculation:
  // containerPadding[1]*2 + maxRows*rowHeight + (maxRows-1)*margin[1]
  const stageHeight = 2 * (20 / zoom) + maxRows * rowHeight + (maxRows - 1) * (12 / zoom);
  return {stageWidth, rowHeight, stageHeight};
};

export const createBlankDashboard = (dataConfig, type = "classic", aspectRatio = "16:9") => {
  const json = {
    dataConfig,
    components: [],
    tabs: [{id: 1, name: "Tab 1", position: 0}],
    layoutType: type === "gamified" ? "gamified" : "infinite",
    type
  };
  if (type === "gamified") {
    json.aspectRatio = aspectRatio;
    json.appearance = {zoom: DEFAULT_GAMIFIED_ZOOM};
  }
  return Immutable.fromJS({
    name: "My New Dashboard",
    configType: "DASHBOARD",
    ownerId: Users.getCurrentUser().get("id"),
    json
  });
};

export const getNextTabId = (tabs) => {
  if (tabs.isEmpty()) return 1;
  return tabs.map(t => t.get("id")).max() + 1;
};

export const getUniqueTabName = (baseName, existingNames) => {
  const taken = Immutable.Set(existingNames);
  if (!taken.has(baseName)) {
    return baseName;
  }
  let index = 2;
  while (taken.has(`${baseName} (${index})`)) {
    index++;
  }
  return `${baseName} (${index})`;
};

// Navigation order: returns the id of the tab following activeTabId by position,
// wrapping from the last tab back to the first. Returns null when there are fewer
// than 2 tabs or activeTabId is not present.
export const getNextTabInOrder = (dashboard, activeTabId) => {
  const tabs = dashboard.getIn(["json", "tabs"], Immutable.List())
      .sortBy(t => t.get("position"));
  if (tabs.size < 2) return null;
  const index = tabs.findIndex(t => t.get("id") === activeTabId);
  if (index === -1) return null;
  const nextIndex = (index + 1) % tabs.size;
  return tabs.getIn([nextIndex, "id"]);
};

export const getNextComponentId = (components) => {
  if (components.isEmpty()) return 1;
  return components.map(c => c.get("id")).max() + 1;
};

export const getComponentsForTab = (components, tabId) =>
  components.filter(c => c.get("tabId") === tabId);

export const normalizeTabPositions = (tabs) => {
  return tabs
      .sortBy(t => t.get("position"))
      .map((t, index) => t.set("position", index));
};

// Copies a tab, its panels and its per-tab settings from one dashboard onto
// another. Tab and component ids are per-dashboard, so both are remapped against
// the target. Returns the new target dashboard plus the id of the tab created.
export const copyTabToDashboard = (sourceDashboard, tabId, targetDashboard) => {
  const sourceTab = sourceDashboard
      .getIn(["json", "tabs"], Immutable.List())
      .find(t => t.get("id") === tabId);
  if (!sourceTab) {
    throw new Error(`Tab ${tabId} not found on source dashboard`);
  }

  const sourceComponents = getComponentsForTab(
      sourceDashboard.getIn(["json", "components"], Immutable.List()), tabId);
  const targetTabs = targetDashboard.getIn(["json", "tabs"], Immutable.List());
  const targetComponents = targetDashboard.getIn(["json", "components"], Immutable.List());

  const newTabId = getNextTabId(targetTabs);
  const lastPosition = targetTabs.map(t => t.get("position")).max();
  const name = getUniqueTabName(
      `${sourceTab.get("name")} (from ${sourceDashboard.get("name")})`,
      targetTabs.map(t => t.get("name")));

  const newTab = sourceTab.merge(Immutable.Map({
    id: newTabId,
    name,
    position: lastPosition === undefined ? 0 : lastPosition + 1
  }));

  const firstComponentId = getNextComponentId(targetComponents);
  const copiedComponents = sourceComponents.map((component, index) =>
      component.set("id", firstComponentId + index).set("tabId", newTabId));

  const dashboard = targetDashboard
      .setIn(["json", "tabs"], normalizeTabPositions(targetTabs.push(newTab)))
      .setIn(["json", "components"], targetComponents.concat(copiedComponents));

  return {dashboard, newTabId};
};

// Dashboards a tab may be copied onto: saved, not the one we're on, not a
// save-restricted system default, and either owned by the current user or
// shared with them for editing. Mirrors the save-permission rule in
// dashboard-sidebar.js so we never offer a destination the user can't save.
//
// A dashboard the caller owns and also shares with themselves (directly or via
// a group) lands in more than one of app.js's ownership-type lists with the
// same id, so we dedupe here rather than relying on callers to pre-clean it.
export const getCopyTargetDashboards = (allDashboards, currentDashboardId, isFullEditor) => {
  const currentUserId = Users.getCurrentUser().get("id");
  const seenIds = new Set();
  return allDashboards.filter(dashboard => {
    const id = dashboard.get("id");
    if (id == null || id === currentDashboardId || dashboard.get("systemDefault") || seenIds.has(id)) {
      return false;
    }
    const qualifies = dashboard.get("ownerId") === currentUserId
        || (isFullEditor
            && dashboard.has("sharedWith")
            && SharingUtils.getCurrentUserPermissionForConfig(dashboard) === "EDIT");
    // Only mark the id seen once it actually qualifies — an earlier occurrence
    // that fails the check must not suppress a later occurrence that passes.
    if (qualifies) {
      seenIds.add(id);
    }
    return qualifies;
  });
};

export const moveTab = (dashboard, tabId, direction) => {
  let tabs = dashboard.getIn(["json", "tabs"], Immutable.List());
  const sorted = tabs.sortBy(t => t.get("position"));
  const currentIndex = sorted.findIndex(t => t.get("id") === tabId);

  switch (direction) {
    case "first": {
      tabs = tabs.map(t => {
        if (t.get("id") === tabId) return t.set("position", -1);
        return t;
      });
      break;
    }
    case "left": {
      if (currentIndex <= 0) return dashboard;
      const swapTab = sorted.get(currentIndex - 1);
      const currentPos = sorted.get(currentIndex).get("position");
      const swapPos = swapTab.get("position");
      tabs = tabs.map(t => {
        if (t.get("id") === tabId) return t.set("position", swapPos);
        if (t.get("id") === swapTab.get("id")) return t.set("position", currentPos);
        return t;
      });
      break;
    }
    case "right": {
      if (currentIndex >= sorted.size - 1) return dashboard;
      const swapTab = sorted.get(currentIndex + 1);
      const currentPos = sorted.get(currentIndex).get("position");
      const swapPos = swapTab.get("position");
      tabs = tabs.map(t => {
        if (t.get("id") === tabId) return t.set("position", swapPos);
        if (t.get("id") === swapTab.get("id")) return t.set("position", currentPos);
        return t;
      });
      break;
    }
    case "last": {
      tabs = tabs.map(t => {
        if (t.get("id") === tabId) return t.set("position", tabs.size + 1);
        return t;
      });
      break;
    }
    default:
      return dashboard;
  }

  return dashboard.setIn(["json", "tabs"], normalizeTabPositions(tabs));
};

export const updateQualifierForLoggedInUserOrGroup = qualifier => {
  if (qualifier.get("id") === 0) {
    if (qualifier.get("type") === "USER") {
      return qualifier.set("id", Users.getCurrentUser().get("id"));
    } else if (qualifier.get("type") === "GROUP") {
      return qualifier.set("id", Users.getCurrentUser().get("groupId"));
    }
  } else {
    return qualifier;
  }
};

export const renderUserOrGroupDetails = (config) => {
  let groupId;
  let isCurrentlyViewingUser;
  const updatedQualifier = updateQualifierForLoggedInUserOrGroup(config.get("qualifier"));
  if (updatedQualifier.get("type") === "USER") {
    groupId = Users.getUser(updatedQualifier.get("id")).get("groupId");
    isCurrentlyViewingUser = true;
  } else {
    groupId = updatedQualifier.get("id");
    isCurrentlyViewingUser = false;
  }

  const breadcrumbs = Groups.getGroupBreadcrumbs(groupId);
  const dividerStyle = {fontSize: "0.725rem", paddingLeft: 5, paddingRight: 5};
  return (
      <span style={{paddingLeft: 5}}>
              {breadcrumbs.map((crumb, i) => {
                const isFirstCrumb = i === 0;
                const isLastCrumb = i === breadcrumbs.length - 1;
                return (
                    <span key={`crumb-${i}`}>
                          {`${isFirstCrumb ? "for " : ""}${crumb.name}`}
                      {!isLastCrumb &&
                          <i className="fa fa-chevron-right" style={dividerStyle} />}
                      </span>
                );
              })}
        {isCurrentlyViewingUser &&
            <span><i className="fa fa-chevron-right" style={dividerStyle} />
              {Users.getUser(updatedQualifier.get("id")).get("fullName")}
                </span>
        }
          </span>);
};

export const configToQualifierAjaxParams = config => {
  const qualifier = updateQualifierForLoggedInUserOrGroup(config.get("qualifier"));
  return qualifierToAjaxParams(qualifier.get("id"), qualifier.get("type"));
};

export const qualifierToAjaxParams = (id, type) => {
  switch (type) {
    case "GROUP":
      return {groupId: id};
    case "USER":
      return {userId: id};
    default:
      throw new Error("unexpected qualifier type: " + type);
  }
};

export const applyParentConfigToDashboard = (dashboard, parentConfig, componentTypeToTemplate) => {
  return dashboard
      .setIn(["json", "dataConfig"], parentConfig)
      .updateIn(["json", "components"], cs => cs.map(component => {
        const template = componentTypeToTemplate.get(component.get("type"));
        const applyParent = template.get("applyParentConfig");
        return component.update("dataConfig", dataConfig => applyParent(parentConfig, dataConfig));
      }));
};

export const LoadingBalls = React.memo(() => {
  return <div className="novo-loading">
    <span className="dot" />
    <span className="dot" />
    <span className="dot" />
    <span className="dot" />
    <span className="dot" />
  </div>;
});

export const useEditorFlags = () => {
  const isFullEditor = React.useMemo(
      () => Users.hasPermission(Users.getCurrentUser(), "DASHBOARD_FULL_EDITOR"),
      []);
  const isBasicEditor = React.useMemo(
      () => isFullEditor || Users.hasPermission(Users.getCurrentUser(), "DASHBOARD_BASIC_EDITOR"),
      [isFullEditor]);
  return {isFullEditor, isBasicEditor};
};

const incrementLoadIdForComponent = (componentIdToLoadIdRef, componentId) => {
  const componentIdToLoadId = componentIdToLoadIdRef.current;
  componentIdToLoadIdRef.current = componentIdToLoadId.update(componentId, 0, x => x + 1);
};

// Every load id must be bumped before the first onDataChange, because that triggers a
// re-render that reads this map. Components with requiresFurtherLoad derive their own
// completion id from it at render time rather than closing over the registered one, so a
// half-updated map lets them signal against an id that was never registered as a load
// start. Nothing can ever clear that id, so the dashboard stays permanently "loading" and
// CUBE19-dashboard-load-complete never fires for scheduled reports.
const bumpLoadIdsForComponents = (componentIdToLoadIdRef, components) => {
  for (const component of components) {
    incrementLoadIdForComponent(componentIdToLoadIdRef, component.get("id"));
  }
};

// Used by scheduled-report mode only (single first-tab dashboard). Normal interactive flow
// uses loadDataForTab via the per-tab cache. Do not call from interactive code paths.
export const loadDataForDashboard = (
    componentIdToLoadIdRef,
    dashboard,
    queueDataLoad,
    setIdToData,
    componentTypeToTemplate,
    onComponentLoadStart,
    onComponentLoadComplete) => {
  let newIdToData = Immutable.Map();
  const components = dashboard.getIn(["json", "components"]).sortBy(component => component.getIn(["layout", "y"]));
  bumpLoadIdsForComponents(componentIdToLoadIdRef, components);
  for (const component of components) {
    const id = getDataId(componentIdToLoadIdRef.current, component);
    const initialData = getInitialComponentData(component, componentTypeToTemplate);
    const template = componentTypeToTemplate.get(component.get("type"));
    const load = template.get("load");
    if (load) {
      newIdToData = newIdToData.set(id, Rata.toLoading(initialData));
      if (onComponentLoadStart) {
        onComponentLoadStart(id);
      }
      queueDataLoad(() => {
        // Promise.resolve().then(() => load(...)) converts synchronous throws into
        // rejected promises so the error handler below always runs and
        // onComponentLoadComplete is always called.
        return Promise.resolve()
            .then(() => load(component))
            .then(
                response => {
                  setIdToData(idToData => idToData.update(id, data => Rata.toLoaded(data, response)));
                  if (onComponentLoadComplete && !template.get("requiresFurtherLoad")) {
                    onComponentLoadComplete(id);
                  }
                },
                error => {
                  setIdToData(idToData => idToData.update(id, data => Rata.toError(data, error)));
                  if (onComponentLoadComplete) {
                    onComponentLoadComplete(id);
                  }
                });
      });
    } else {
      newIdToData = newIdToData.set(id, Rata.toLoaded(initialData, Immutable.Map()));
    }
  }
  setIdToData(newIdToData);
};

export const loadDataForTab = (
    componentIdToLoadIdRef,
    dashboard,
    tabId,
    queueDataLoad,
    onDataChange,
    componentTypeToTemplate,
    getPanelWeight,
    onComponentLoadStart,
    onComponentLoadComplete) => {
  const allComponents = dashboard.getIn(["json", "components"]);
  const components = getComponentsForTab(allComponents, tabId).sortBy(component => component.getIn(["layout", "y"]));
  bumpLoadIdsForComponents(componentIdToLoadIdRef, components);
  for (const component of components) {
    const id = getDataId(componentIdToLoadIdRef.current, component);
    const initialData = getInitialComponentData(component, componentTypeToTemplate);
    const template = componentTypeToTemplate.get(component.get("type"));
    const load = template.get("load");
    if (load) {
      onDataChange(tabId, id, Rata.toLoading(initialData), getPanelWeight(component.get("type")));
      if (onComponentLoadStart) {
        onComponentLoadStart(id);
      }
      queueDataLoad(() => {
        return Promise.resolve()
            .then(() => load(component))
            .then(
                response => {
                  onDataChange(tabId, id, Rata.toLoaded(initialData, response), getPanelWeight(component.get("type")));
                  if (onComponentLoadComplete && !template.get("requiresFurtherLoad")) {
                    onComponentLoadComplete(id);
                  }
                },
                error => {
                  onDataChange(tabId, id, Rata.toError(initialData, error), 0);
                  if (onComponentLoadComplete) {
                    onComponentLoadComplete(id);
                  }
                });
      }, {componentId: component.get("id"), componentLoadId: componentIdToLoadIdRef.current.get(component.get("id")), tabId});
    } else {
      onDataChange(tabId, id, Rata.toLoaded(initialData, Immutable.Map()), 0);
    }
  }
};

export const loadDataForComponent = (
    componentId,
    componentIdToLoadIdRef,
    dashboard,
    queueDataLoad,
    onDataChange,
    componentTypeToTemplate,
    getPanelWeight
) => {
  const component = dashboard.getIn(["json", "components"], Immutable.List()).find(c => c.get("id") === componentId);
  const tabId = component.get("tabId");
  incrementLoadIdForComponent(componentIdToLoadIdRef, component.get("id"));
  const dataId = getDataId(componentIdToLoadIdRef.current, component);
  const load = componentTypeToTemplate.get(component.get("type")).get("load");
  const initialData = getInitialComponentData(component, componentTypeToTemplate);
  if (load) {
    onDataChange(tabId, dataId, Rata.toLoading(initialData), getPanelWeight(component.get("type")));
    queueDataLoad(() => {
      return load(component).then(
          response => {
            onDataChange(tabId, dataId, Rata.toLoaded(initialData, response), getPanelWeight(component.get("type")));
          },
          error => {
            onDataChange(tabId, dataId, Rata.toError(initialData, error), 0);
          });
    }, {componentId: component.get("id"), componentLoadId: componentIdToLoadIdRef.current.get(component.get("id")), tabId});
  } else {
    onDataChange(tabId, dataId, Rata.toLoaded(initialData, Immutable.Map()), 0);
  }
};

export const useDataLoadingQueue = () => {
  const concurrency = 4;
  const fnQueueRef = React.useRef({running: 0, queue: []});

  const ensureRunning = React.useCallback(() => {
    const {queue} = fnQueueRef.current;

    while (queue.length > 0 && fnQueueRef.current.running < concurrency) {
      fnQueueRef.current.running++;
      const {fn, promiseResolve, promiseReject} = queue.shift();
      // Use async IIFE so synchronous throws from fn() are caught and treated as
      // rejections — without this, a synchronous throw leaks out of ensureRunning,
      // leaves running incremented forever, and starves the rest of the queue.
      (async () => {
        try {
          promiseResolve(await fn());
        } catch (error) {
          promiseReject(error);
        } finally {
          fnQueueRef.current.running--;
          ensureRunning();
        }
      })();
    }
  }, []);

  const clear = React.useCallback((shouldDropItemFn = () => true) => {
    fnQueueRef.current.queue = fnQueueRef.current.queue.filter(item => !shouldDropItemFn(item));
  }, []);

  const queue = React.useCallback((fn, {componentId, componentLoadId, tabId, priority = false} = {}) => {
    const {queue} = fnQueueRef.current;
    let promiseResolve;
    let promiseReject;
    const promise = new Promise((resolve, reject) => {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    const item = {fn, componentId, componentLoadId, tabId, promiseResolve, promiseReject};
    if (priority) {
      queue.unshift(item);
    } else {
      queue.push(item);
    }
    ensureRunning();
    return promise;
  }, [ensureRunning]);

  const prioritiseTab = React.useCallback((tabId) => {
    const current = fnQueueRef.current.queue;
    const matching = [];
    const others = [];
    for (const item of current) {
      if (item.tabId === tabId) {
        matching.push(item);
      } else {
        others.push(item);
      }
    }
    fnQueueRef.current.queue = [...matching, ...others];
  }, []);

  const dropForTab = React.useCallback((tabId) => {
    fnQueueRef.current.queue = fnQueueRef.current.queue.filter(item => item.tabId !== tabId);
  }, []);

  return {
    clear,
    queue,
    prioritiseTab,
    dropForTab
  };
};

export const getInitialComponentData = (component, componentTypeToTemplate) => {
  const template = componentTypeToTemplate.get(component.get("type"));
  return Rata.wrapInitialValue(template.get("getDefaultData")());
};

export const getInitialComponentIdToData = (componentIdToLoadId, dashboard, componentTypeToTemplate) => {
  return dashboard
      .getIn(["json", "components"])
      .reduce(
          (m, component) => {
            const initialData = getInitialComponentData(component, componentTypeToTemplate);
            return m.set(getDataId(componentIdToLoadId, component), initialData);
          },
          Immutable.Map());
};

export const isBasicEditor = () => Users.getCurrentUser().hasPermission("DASHBOARD_BASIC_EDITOR");

export const dataId = (loadId, componentId) => "component" + componentId + "_load" + loadId;

export const getDataId = (componentIdToLoadId, component) => {
  const componentId = component.get("id");
  const loadId = componentIdToLoadId.get(componentId, 0);
  return dataId(loadId, componentId);
};

export const getDefaultParentConfig = () => {
  const currentUser = Users.getCurrentUser();
  let qualifier;
  if (currentUser.get("dataVisibility") === "SELF") {
    qualifier = {
      type: "USER",
      id: currentUser.get("id")
    };
  } else {
    qualifier = {
      type: "GROUP",
      id: Groups.getRootGroup().get("id")
    };
  }
  return Immutable.fromJS({
    timeframe: TimeframeRepo.getDefaultForClient().getRawJson(),
    tagFilter: {
      matchAnyTagIds: Immutable.Set(),
      matchAllTagIds: Immutable.Set(),
      excludedTagIds: Immutable.Set()
    },
    clientFilter: {
      allClientIds: Immutable.Set(),
      clientIds: Immutable.Set(),
      clientSetIds: Immutable.Set()
    },
    qualifier
  });
};

export const defaultInheritKeys = Immutable.Set(["timeframe", "qualifier", "tagFilter", "clientFilter"]);

const alwaysInheritKeys = Immutable.Set(["entityToIds"]);

export const applyInheritanceForKeys = (parentConfig, componentDataConfig) => componentDataConfig
    .get("inheritKeys", Immutable.Set())
    .toSet()
    .union(alwaysInheritKeys)
    .reduce(
        (c, key) => {
          if (parentConfig.has(key)) {
            return c.set(key, parentConfig.get(key));
          } else {
            return c;
          }
        },
        componentDataConfig);

export const lockPageScroll = () => {
  window.scrollTo(0, 0);
  document.querySelector("body").style.overflowY = "hidden";
  document.querySelector("body").classList.add("forceOverflowHidden");
};

export const enablePageScroll = () => {
  document.querySelector("body").style.overflowY = "auto";
  document.querySelector("body").classList.remove("forceOverflowHidden");
};
